<script lang="ts">
  import {
    Cobros, Cuentas, Pacientes, FarmaciaClinica,
    type ResumenCobros, type Paciente, type CerrarCobroBody, type DocumentoFiscal, type Cuenta,
  } from '../lib/api';
  import { etiquetaPersona } from '../lib/nombres';
  import PacientePicker from './PacientePicker.svelte';
  import ModalTipoComprobante, { type TipoComprobante } from './ModalTipoComprobante.svelte';
  import ReciboImprimible from './ReciboImprimible.svelte';

  type Linea = {
    key: string;
    que: string;
    origen: string;
    cantidad: number;
    precio: number;
    total: number;
  };

  let pacienteId = $state('');
  let paciente = $state<Paciente | null>(null);
  let resumen = $state<ResumenCobros | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let cargoDesc = $state('Consulta médica');
  let cargoPrecio = $state(350);
  let agregando = $state(false);
  let formaPago = $state<'EFECTIVO' | 'TARJETA' | 'TRANSFERENCIA'>('EFECTIVO');
  let modalAbierto = $state(false);
  let procesando = $state(false);
  let ultimoDocumento = $state<DocumentoFiscal | null>(null);
  let lineasImpresion = $state<Array<{ descripcion: string; cantidad: number; precioUnitario: number; subtotal?: number }>>([]);

  const cuenta = $derived(resumen?.cuentasAbiertas?.[0] ?? null);
  const total = $derived(Number(resumen?.montoTotalPendiente ?? 0));
  const lineas = $derived.by((): Linea[] => {
    const out: Linea[] = [];
    for (const r of cuenta?.renglones ?? []) {
      out.push({
        key: r.id ?? `c-${r.descripcion}`,
        que: r.descripcion || 'Cargo',
        origen: etiquetaOrigen(r.centroIngreso, r.descripcion),
        cantidad: Number(r.cantidad ?? 1),
        precio: Number(r.precioUnitario ?? 0),
        total: Number(r.total ?? Number(r.cantidad ?? 1) * Number(r.precioUnitario ?? 0)),
      });
    }
    for (const v of resumen?.ventasPendientes ?? []) {
      out.push({
        key: v.id ?? `v-${v.fecha}`,
        que: v.notas?.trim() || 'Medicamentos / farmacia',
        origen: 'Farmacia',
        cantidad: 1,
        precio: Number(v.total ?? 0),
        total: Number(v.total ?? 0),
      });
    }
    return out;
  });

  function etiquetaOrigen(c?: string | null, desc?: string): string {
    const m: Record<string, string> = {
      LABORATORIO: 'Laboratorio',
      IMAGENOLOGIA: 'Imagenología',
      HONORARIO: 'Honorario médico',
      FARMACIA: 'Farmacia',
      CONSULTA: 'Consulta',
      URGENCIA: 'Urgencia',
      HOSPITALIZACION: 'Hospitalización',
    };
    if (c && m[c]) return m[c];
    const d = (desc ?? '').toLowerCase();
    if (d.includes('consulta')) return 'Consulta';
    if (d.includes('urgencia')) return 'Urgencia';
    if (d.includes('lab')) return 'Laboratorio';
    if (d.includes('farmacia') || d.includes('receta')) return 'Farmacia';
    return 'Servicio';
  }

  function inferirCentro(desc: string): string | undefined {
    const d = desc.toLowerCase();
    if (d.includes('consulta')) return 'CONSULTA';
    if (d.includes('urgencia')) return 'URGENCIA';
    if (d.includes('lab')) return 'LABORATORIO';
    if (d.includes('rayos') || d.includes('imagen') || d.includes('eco')) return 'IMAGENOLOGIA';
    if (d.includes('receta') || d.includes('farmacia')) return 'FARMACIA';
    return undefined;
  }

  function fmtQ(n: number | null | undefined): string {
    return `Q ${Number(n ?? 0).toFixed(2)}`;
  }

  async function cargarResumen() {
    if (!pacienteId) {
      resumen = null;
      return;
    }
    loading = true;
    error = null;
    try {
      resumen = await Cobros.resumen(pacienteId);
    } catch (e: any) {
      error = e?.message ?? String(e);
      resumen = null;
    } finally {
      loading = false;
    }
  }

  function onPick(id: string, p?: Paciente) {
    pacienteId = id;
    paciente = p ?? null;
    ultimoDocumento = null;
    info = null;
    error = null;
    cargoDesc = 'Consulta médica';
    cargoPrecio = 350;
    if (!id) {
      resumen = null;
      return;
    }
    void (async () => {
      if (!p && id) {
        paciente = await Pacientes.uno(id).catch(() => null);
      }
      await cargarResumen();
    })();
  }

  async function asegurarCuenta(): Promise<string> {
    if (cuenta?.id) return cuenta.id;
    const abierta = await Cuentas.abrir({ pacienteId });
    if (!abierta.id) throw new Error('No se pudo abrir la cuenta del paciente.');
    return abierta.id;
  }

  async function agregarCargo(desc = cargoDesc, precio = cargoPrecio, centro?: string) {
    if (!pacienteId) { error = 'Busque al paciente primero.'; return; }
    const d = desc.trim();
    if (!d) { error = 'Escriba qué se está cobrando.'; return; }
    if (Number(precio) <= 0) { error = 'Indique el precio del cargo.'; return; }
    agregando = true;
    error = null;
    info = null;
    try {
      const cuentaId = await asegurarCuenta();
      await Cuentas.renglon(cuentaId, {
        descripcion: d,
        cantidad: 1,
        precioUnitario: Number(precio),
        centroIngreso: centro || inferirCentro(d),
      });
      cargoDesc = 'Consulta médica';
      cargoPrecio = 350;
      await cargarResumen();
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      agregando = false;
    }
  }

  function abrirCobro() {
    if (!pacienteId) { error = 'Busque al paciente primero.'; return; }
    if (total <= 0) { error = 'Agregue un cargo antes de cobrar.'; return; }
    error = null;
    modalAbierto = true;
  }

  async function armarLineasImpresion(ventaIds: string[], c: Cuenta | null) {
    const lineasDoc: typeof lineasImpresion = [];
    for (const r of c?.renglones ?? []) {
      lineasDoc.push({
        descripcion: r.descripcion,
        cantidad: Number(r.cantidad),
        precioUnitario: Number(r.precioUnitario),
        subtotal: Number(r.total ?? r.cantidad * r.precioUnitario),
      });
    }
    for (const vid of ventaIds) {
      try {
        const det = await FarmaciaClinica.venta(vid);
        for (const it of det.items ?? []) {
          lineasDoc.push({
            descripcion: it.descripcion ?? 'Ítem farmacia',
            cantidad: Number(it.cantidad ?? 1),
            precioUnitario: Number(it.precioUnitario ?? 0),
            subtotal: Number(it.subtotal ?? 0),
          });
        }
      } catch { /* omit */ }
    }
    lineasImpresion = lineasDoc;
  }

  async function confirmarCobro(tipo: TipoComprobante, fel?: { nit: string; nombre: string }) {
    procesando = true;
    error = null;
    info = null;
    const cuentaSnapshot = cuenta;
    try {
      const body: CerrarCobroBody = {
        pacienteId,
        tipoDocumento: tipo,
        formaPago,
        cuentaId: cuenta?.id,
        ventaIds: (resumen?.ventasPendientes ?? []).map(v => v.id!).filter(Boolean),
        receptorNit: fel?.nit,
        receptorNombre: fel?.nombre,
      };
      const r = await Cobros.cerrar(body);
      modalAbierto = false;
      if (tipo === 'POSTERGAR') {
        info = `Cobro guardado para más tarde · ${fmtQ(r.total)}`;
        ultimoDocumento = null;
      } else if (r.documento) {
        ultimoDocumento = r.documento;
        await armarLineasImpresion(body.ventaIds ?? [], cuentaSnapshot);
        const docLabel = r.documento.tipo === 'FACTURA' ? 'Factura' : 'Recibo';
        info = `Cobrado ${fmtQ(r.documento.total)} · ${docLabel} ${r.documento.serie}-${r.documento.numero}`;
      } else {
        info = `Cobrado ${fmtQ(r.total)}`;
      }
      await cargarResumen();
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      procesando = false;
    }
  }
</script>

<section>
  <header class="view-header">
    <div>
      <h2>Cobrar</h2>
      <p class="muted">Consultas y servicios del paciente. Los medicamentos del mostrador se cobran en <strong>Farmacia POS</strong>.</p>
    </div>
  </header>

  {#if error}<p class="err" role="alert">⚠ {error}</p>{/if}
  {#if info}<p class="ok" role="status">{info}</p>{/if}

  <div class="picker-row">
    <label class="field grow">
      <span>Paciente</span>
      <PacientePicker
        bind:value={pacienteId}
        onPick={onPick}
        placeholder="Nombre o CUI…"
      />
    </label>
    {#if pacienteId}
      <label class="field">
        <span>Pago</span>
        <select bind:value={formaPago}>
          <option value="EFECTIVO">Efectivo</option>
          <option value="TARJETA">Tarjeta</option>
          <option value="TRANSFERENCIA">Transferencia</option>
        </select>
      </label>
    {/if}
  </div>

  {#if !pacienteId}
    <p class="guia">1. Paciente · 2. Qué se cobra · 3. Cobrar</p>
  {:else}
    <article class="caja">
      <div class="caja-head">
        <div>
          <strong>{paciente ? etiquetaPersona(paciente) : 'Paciente'}</strong>
          {#if paciente?.cui}<span class="meta">CUI {paciente.cui}</span>{/if}
          {#if paciente?.telefono}<span class="meta">Tel. {paciente.telefono}</span>{/if}
        </div>
        <strong class="total">{fmtQ(total)}</strong>
      </div>

      {#if loading && !resumen}
        <p class="muted">Cargando…</p>
      {:else}
        <table class="renglones">
          <thead>
            <tr>
              <th>Qué se cobra</th>
              <th>Origen</th>
              <th>Cant.</th>
              <th>P.U.</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {#each lineas as ln (ln.key)}
              <tr>
                <td><strong>{ln.que}</strong></td>
                <td>{ln.origen}</td>
                <td class="num">{ln.cantidad}</td>
                <td class="num">{fmtQ(ln.precio)}</td>
                <td class="num">{fmtQ(ln.total)}</td>
              </tr>
            {:else}
              <tr><td colspan="5" class="empty">Nada pendiente. Agregue un cargo abajo.</td></tr>
            {/each}
          </tbody>
        </table>

        <div class="add">
          <span class="hint">Agregar</span>
          <button type="button" class="ghost sm" disabled={agregando} onclick={() => agregarCargo('Consulta médica', 350, 'CONSULTA')}>Consulta Q350</button>
          <button type="button" class="ghost sm" disabled={agregando} onclick={() => agregarCargo('Urgencia', 500, 'URGENCIA')}>Urgencia Q500</button>
          <input class="desc" placeholder="Otro cargo…" bind:value={cargoDesc} />
          <input class="precio" type="number" min="0" step="0.01" bind:value={cargoPrecio} />
          <button type="button" class="ghost" disabled={agregando} onclick={() => agregarCargo()}>
            {agregando ? '…' : '+ Agregar'}
          </button>
        </div>

        <div class="acciones">
          <button type="button" class="primary cobrar" disabled={total <= 0 || procesando} onclick={abrirCobro}>
            Cobrar {fmtQ(total)}
          </button>
        </div>
      {/if}
    </article>
  {/if}

  {#if ultimoDocumento}
    <ReciboImprimible
      documento={ultimoDocumento}
      pacienteNombre={paciente ? etiquetaPersona(paciente) : ''}
      lineas={lineasImpresion}
      onClose={() => { ultimoDocumento = null; }}
    />
  {/if}
</section>

<ModalTipoComprobante
  bind:open={modalAbierto}
  {total}
  procesando={procesando}
  hint="Recibo es el comprobante habitual. Use factura FEL si el paciente la pide."
  allowPostergar={false}
  onConfirm={confirmarCobro}
/>

<style>
  .view-header { margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; }
  .muted { margin: 0.25rem 0 0; color: var(--text-secondary, #6b7280); font-size: 0.9rem; }
  .picker-row { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; margin-bottom: 1rem; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field.grow { flex: 1; min-width: 16rem; }
  .field span, .hint { font-size: .72rem; font-weight: 600; color: var(--text-secondary, #6b7280); text-transform: uppercase; letter-spacing: .03em; }
  .guia { color: var(--text-secondary, #6b7280); font-size: 1rem; padding: 1.5rem 0; }
  input, select, button {
    padding: .5rem .65rem; border-radius: 8px; border: 1px solid var(--border, #e5e7eb);
    font: inherit; background: var(--bg-elevated, #fff); color: inherit;
  }
  input:focus, select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  button { cursor: pointer; font-weight: 600; }
  button:disabled { opacity: .5; cursor: not-allowed; }
  button.primary {
    background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border: 0;
    box-shadow: 0 4px 14px rgba(22,163,74,0.3);
  }
  button.ghost { background: var(--bg-elevated, #fff); color: inherit; border: 1px solid var(--border, #e5e7eb); box-shadow: none; }
  .ghost.sm { font-size: .8rem; padding: .3rem .55rem; }
  .err { color: #dc2626; font-weight: 600; }
  .ok { color: #166534; font-weight: 600; }
  .caja { border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1rem; background: var(--bg-elevated, #fff); }
  .caja-head { display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; flex-wrap: wrap; margin-bottom: .75rem; }
  .caja-head strong { font-size: 1.05rem; }
  .meta { margin-left: .6rem; font-size: .85rem; color: var(--text-secondary, #6b7280); font-weight: 500; }
  .total { font-size: 1.35rem; color: #15803d; }
  .renglones { width: 100%; border-collapse: collapse; }
  .renglones th {
    text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em;
    padding: .45rem .55rem; background: color-mix(in srgb, #16a34a 16%, transparent);
  }
  .renglones td { padding: .45rem .55rem; border-top: 1px solid var(--border, #f3f4f6); font-size: .92rem; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .empty { color: var(--text-secondary, #9ca3af); font-style: italic; text-align: center; }
  .add { display: flex; flex-wrap: wrap; gap: .4rem; align-items: center; margin-top: .85rem; }
  .add .desc { flex: 1; min-width: 10rem; }
  .add .precio { width: 6.5rem; }
  .acciones { display: flex; justify-content: flex-end; margin-top: 1rem; }
  .cobrar { font-size: 1.05rem; padding: .7rem 1.4rem; }
</style>
